import * as yup from "yup";
import {
  createAddressFields,
  CHAPTER_3_STATUS_OPTIONS,
  CHAPTER_4_STATUS_OPTIONS,
  LOB_TYPES_OPTIONS,
} from "../constants";
import {
  type FormFieldConfig,
  type FormSectionConfig,
  type TaxFormConfig,
  type TaxFormValues,
} from "../types";

// Chapter 4 statuses that do NOT show any FATCA part section
const CHAPTER_4_EXCLUDED_STATUSES = [
  "nonparticipating_ffi",
  "participating_ffi",
  "reporting_model_1_ffi",
  "reporting_model_2_ffi",
  "registered_deemed_compliant_ffi",
  "direct_reporting_nffe",
  "account_not_financial_account",
];

const getChapter4StatusLabel = (value: string): string => {
  const option = CHAPTER_4_STATUS_OPTIONS.find((opt) => opt.value === value);
  return option?.label ?? "";
};

/*
 * FATCA Part Definitions (Part IV - XXVIII)
 * Each entry maps a Chapter 4 status to its Part number and confirmation fields
 */
type FATCAPartConfig = {
  statusValue: string;
  partNumber: string; // e.g., "IV", "V", "VI"
  fields?: FormFieldConfig[]; // Additional fields beyond the standard confirmation
};

export const FATCA_PARTS: FATCAPartConfig[] = [
  { statusValue: "sponsored_ffi", partNumber: "IV" },
  { statusValue: "cert_dc_nonreg_local_bank", partNumber: "V" },
  { statusValue: "cert_dc_ffi_low_value_accounts", partNumber: "VI" },
  { statusValue: "cert_dc_sponsored_closely_held_inv_vehicle", partNumber: "VII" },
  { statusValue: "cert_dc_limited_life_debt_inv_entity", partNumber: "VIII" },
  { statusValue: "certain_inv_entities_no_financial_accounts", partNumber: "IX" },
  { statusValue: "owner_documented_ffi", partNumber: "X" },
  { statusValue: "restricted_distributor", partNumber: "XI" },
  { statusValue: "nonreporting_iga_ffi", partNumber: "XII" },
  { statusValue: "foreign_govt_us_possession_or_central_bank", partNumber: "XIII" },
  { statusValue: "international_organization", partNumber: "XIV" },
  { statusValue: "exempt_retirement_plans", partNumber: "XV" },
  { statusValue: "entity_wholly_owned_by_exempt_beneficial_owners", partNumber: "XVI" },
  { statusValue: "territory_financial_institution", partNumber: "XVII" },
  { statusValue: "excepted_nonfinancial_group_entity", partNumber: "XVIII" },
  { statusValue: "excepted_nonfinancial_start_up_company", partNumber: "XIX" },
  { statusValue: "excepted_nonfin_entity_liquidation_bankruptcy", partNumber: "XX" },
  { statusValue: "section_501_c_organization", partNumber: "XXI" },
  { statusValue: "nonprofit_organization", partNumber: "XXII" },
  { statusValue: "publicly_traded_nffe_or_affiliate", partNumber: "XXIII" },
  { statusValue: "excepted_territory_nffe", partNumber: "XXIV" },
  { statusValue: "active_nffe", partNumber: "XXV" },
  { statusValue: "passive_nffe", partNumber: "XXVI" },
  { statusValue: "excepted_inter_affiliate_ffi", partNumber: "XXVII" },
  { statusValue: "sponsored_direct_reporting_nffe", partNumber: "XXVIII" },
];

// Factory to create a FATCA part section
const createFATCAPartSection = (config: FATCAPartConfig): FormSectionConfig => ({
  id: `chapter4Part${config.partNumber}`,
  title: (values: TaxFormValues) => {
    const status = typeof values.chapter4Status === "string" ? values.chapter4Status : "";
    return getChapter4StatusLabel(status);
  },
  showWhen: (values: TaxFormValues) => {
    const chapter4Status =
      typeof values.chapter4Status === "string" ? values.chapter4Status : "";
    return chapter4Status === config.statusValue;
  },
  fields: [
    {
      name: `_part${config.partNumber}Description`,
      type: "description",
      label: `This section is related to Chapter 4 status selection:`,
      gridColumn: "full",
    },
    {
      name: `chapter4Part${config.partNumber}Confirmed`,
      type: "checkbox",
      label: `I confirm the required fields in Part ${config.partNumber} are completed.`,
      tooltip: {
        content: `Navigate to Part ${config.partNumber} of the form and confirm the section is completed correctly.`,
        placement: "bottom",
      },
      required: true,
      gridColumn: "full",
    },
    ...(config.fields ?? []),
  ],
});

// Generate all FATCA part sections
const fatcaPartSections: FormSectionConfig[] = FATCA_PARTS.map(createFATCAPartSection);

const sections: FormSectionConfig[] = [
  {
    id: "identification",
    title: "Identification of Beneficial Owner",
    fields: [
      {
        name: "entityName",
        type: "text",
        label: "Entity name",
        placeholder: "Enter entity name",
        required: true,
        gridColumn: "half",
      },
      {
        name: "countryOfIncorporation",
        type: "country",
        label: "Country of incorporation",
        placeholder: "Select a country",
        required: true,
        gridColumn: "half",
      },
      {
        name: "disregardedEntityName",
        type: "text",
        label: "Disregarded entity name",
        gridColumn: "full",
      },
      {
        name: "chapter3Status",
        type: "select",
        label: "Chapter 3 status",
        required: true,
        gridColumn: "full",
        options: CHAPTER_3_STATUS_OPTIONS,
      },
      {
        name: "chapter4Status",
        type: "select",
        label: "Chapter 4 status (FATCA)",
        required: true,
        gridColumn: "full",
        options: CHAPTER_4_STATUS_OPTIONS,
      },
      {
        name: "_chapter4InfoBanner",
        type: "info",
        label: (values: TaxFormValues) => {
          const status = typeof values.chapter4Status === "string" ? values.chapter4Status : "";
          const statusLabel = getChapter4StatusLabel(status);
          return `Note: Based on the Chapter 4 status selection, please review and confirm the required ${statusLabel} section below.`;
        },
        gridColumn: "full",
        showWhen: (values: TaxFormValues) => {
          const chapter4Status =
            typeof values.chapter4Status === "string" ? values.chapter4Status : "";
          return !!chapter4Status && !CHAPTER_4_EXCLUDED_STATUSES.includes(chapter4Status);
        },
      },
      {
        name: "_permanentAddressLabel",
        type: "label",
        label: "Permanent residence address",
        required: true,
        linkedField: "permanentAddress",
        gridColumn: "full",
      },
      ...createAddressFields("permanentAddress", {
        helpText:
          "Do not use a P.O. box or in-care-of address (other than a registered address)",
      }),
      {
        name: "usTin",
        type: "text",
        label: "US TIN",
        gridColumn: "half",
      },
      {
        name: "_usTinSpacer",
        type: "label",
        label: "",
        gridColumn: "half",
      },
      {
        name: "giin",
        type: "giin",
        label: "GIIN",
        placeholder: "XXXXXX-XXXXX-XX-XXX",
        tooltip: {
          content:
            "GIIN is a 19-character identifier (including dots) and cannot contain the letter 'O'.",
        },
        gridColumn: "half",
      },
      {
        name: "foreignTin",
        type: "text",
        label: "Foreign TIN",
        gridColumn: "half",
      },
      {
        name: "giinVerified",
        type: "checkbox",
        label: "Check if GIIN has been verified through the IRS portal",
        gridColumn: "full",
      },
    ],
  },
  {
    id: "sponsor",
    title: "Sponsor",
    fields: [
      {
        name: "sponsorName",
        type: "text",
        label: "Sponsor name",
        gridColumn: "half",
      },
    ],
  },
  {
    id: "treatyBenefits",
    title: "Claim of Tax Treaty Benefits",
    description: "Select all that apply",
    fields: [
      {
        name: "isResidentOfTreatyCountry",
        type: "checkbox",
        label:
          "The beneficial owner is a resident of the following country within the meaning of the income tax treaty between the United States and that country:",
        gridColumn: "full",
      },
      {
        name: "treatyCountry",
        type: "country",
        label: "",
        placeholder: "Select a country",
        gridColumn: "half",
      },
      {
        name: "hasLimitationOnBenefits",
        type: "checkbox",
        label:
          "The beneficial owner derives the item (or items) of income for which the treaty benefits are claimed, and if applicable, meets the requirements of the treaty provision dealing with limitation on benefits. The following are types of limitation on benefits provisions that may be included in an applicable tax treaty:",
        gridColumn: "full",
      },
      {
        name: "lobType",
        type: "select",
        label: "Types of limitation on benefits provisions",
        placeholder: "Select lob type",
        gridColumn: "full",
        options: LOB_TYPES_OPTIONS,
      },
      {
        name: "lobArticleAndParagraph",
        type: "text",
        label: "",
        placeholder: "Specify Article and paragraph",
        gridColumn: "full",
        showWhen: (values) => values.lobType === "other",
      },
      {
        name: "claimsDividendsBenefits",
        type: "checkbox",
        label:
          "The beneficial owner is claiming treaty benefits for U.S. source dividends received from a foreign corporation or interest from a U.S. trade or business of a foreign corporation and meets qualified resident status.",
        gridColumn: "full",
      },
      {
        name: "treatyRateClaimed",
        type: "percentage",
        label: "Treaty rate claimed",
        placeholder: "Enter percentage",
        gridColumn: "half",
      },
      {
        name: "incomeType",
        type: "text",
        label: "Income type",
        placeholder: "Specify type of income",
        gridColumn: "half",
      },
    ],
  },
  // FATCA Part sections (Part IV - XXVIII) - generated dynamically
  ...fatcaPartSections,
  {
    id: "fatcaSubstantialOwners",
    title: "FATCA SUBSTANTIAL OWNERS",
    actionLabel: "Add substantial owner",
    tooltip: {
      content:
        "The FATCA substantial owners section identifies individuals defined within a W-8BEN-E form as a Substantial US Owner.",
    },
    fields: [
      {
        name: "fatcaSubstantialOwners",
        type: "controllingPersons",
        label: "FATCA SUBSTANTIAL OWNERS",
        gridColumn: "full",
        emptyStateText: "There are no substantial owners.",
        infoText:
          "Ensure the information below matches the FATCA tax document. Any information saved here is specific to this FATCA substantial owner.",
        addButtonText: "Add substantial owner",
        updateButtonText: "Update substantial owner",
        searchLabel: "Search existing profile or add new substantial owner",
        showDateOfBirth: false,
        searchInputPlaceholder: "Enter substantial owner name",
        searchHelperText:
          "Using an existing profile only pre-fills the substantial owner information and does not link to the profile used. If the substantial owner is not listed, enter details manually.",
      },
    ],
  },
];

// Generate validation for each FATCA part confirmation checkbox
const fatcaPartValidations = FATCA_PARTS.reduce(
  (acc, config) => ({
    ...acc,
    [`chapter4Part${config.partNumber}Confirmed`]: yup.boolean().when("chapter4Status", {
      is: (status: string) => status === config.statusValue,
      then: (schema) =>
        schema.oneOf([true], "Please confirm the required fields are completed"),
    }),
  }),
  {} as Record<string, yup.BooleanSchema>,
);

const validationSchema = yup.object().shape({
  entityName: yup.string().required("Entity name is required"),
  countryOfIncorporation: yup.string().required("Country of incorporation is required"),
  chapter3Status: yup.string().required("Chapter 3 status is required"),
  chapter4Status: yup.string().required("Chapter 4 status is required"),
  permanentAddress: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  giinVerified: yup.boolean().when("giin", {
    is: (giin: string) => Boolean(giin?.trim()),
    then: (schema) =>
      schema.test("giin-validation", "", function (value) {
        const { giin } = this.parent as { giin: string };
        if (giin?.trim() && giin.replace(/\./g, "").length !== 16) {
          return this.createError({ message: "GIIN provided is incomplete" });
        }
        if (value !== true) {
          return this.createError({
            message: "GIIN provided must be verified through the IRS portal",
          });
        }
        return true;
      }),
  }),
  ...fatcaPartValidations,
});

// Generate initial values for each FATCA part confirmation checkbox
const fatcaPartInitialValues = FATCA_PARTS.reduce(
  (acc, config) => ({
    ...acc,
    [`chapter4Part${config.partNumber}Confirmed`]: false,
  }),
  {} as Record<string, boolean>,
);

const initialValues = {
  entityName: "",
  countryOfIncorporation: "",
  disregardedEntityName: "",
  chapter3Status: "",
  chapter4Status: "",
  permanentAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  usTin: "",
  giin: "",
  foreignTin: "",
  giinVerified: false,
  sponsorName: "",
  isResidentOfTreatyCountry: false,
  treatyCountry: "",
  hasLimitationOnBenefits: false,
  lobType: "",
  lobArticleAndParagraph: "",
  claimsDividendsBenefits: false,
  treatyRateClaimed: "",
  incomeType: "",
  fatcaSubstantialOwners: [],
  ...fatcaPartInitialValues,
};

export const w8beneConfig: TaxFormConfig = {
  formId: "W8BENE",
  title: "New FATCA details",
  subtitle: "W-8BEN-E",
  sections,
  validationSchema,
  initialValues,
};
